import { Css, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { AddProductOfferingConfig_ReadyPlanOptionsFragment } from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { ConfigOptionsAndDelta, ProductOfferingConfigForm } from "../AddProductOfferingConfigPage";
import { OptionSection } from "./OptionSection";

export type GroupedOptionsProps = {
  groupName?: string;
  groupedRpos: Record<string, ConfigOptionsAndDelta[]>;
  formState: ObjectState<ProductOfferingConfigForm>;
  recomputeCosts: (
    rpo: AddProductOfferingConfig_ReadyPlanOptionsFragment,
    isSelected: boolean,
    prevMultiSelectRpoId?: string,
  ) => Promise<void>;
};

export function OptionsByOptionGroupSection(props: GroupedOptionsProps) {
  const { groupedRpos: rposGroupedByOptionGroup, formState, recomputeCosts, groupName } = props;
  const selectedRpos = useComputed(
    () => formState.value.readyPlanOptions.map((os) => os.readyPlanOptionId).compact(),
    [formState],
  );
  const isCreatingBaseConfig = useComputed(() => !!formState.value.isBaseConfig, [formState]);

  return (
    <div css={Css.bgWhite.aic.$}>
      <div css={Css.gc(2).$}>{groupName && <h3 css={Css.mdSb.mb3.$}>{groupName}</h3>}</div>

      {Object.entries(rposGroupedByOptionGroup).map(([OptGroupName, optGroupRpos]) => (
        <div key={[OptGroupName, ...optGroupRpos.map((delta) => delta.rpo.id)].join("-")}>
          {optGroupRpos
            .sortBy((data) => data.rpo.order ?? Number.POSITIVE_INFINITY)
            .map((data) => {
              const conflicts = [
                ...data.rpo.optionConflicts.filter((opt) => selectedRpos.includes(opt.id)),
                ...data.rpo.optionConflictChildren.filter((opt) => selectedRpos.includes(opt.id)),
              ].map((opt) => opt.displayName);
              // Cannot select until these items are selected
              const missingPreReqs = [
                ...data.rpo.optionPrerequisites.filter(
                  (preReqOpt) =>
                    // Find any prereq not yet selected,
                    !selectedRpos.includes(preReqOpt.id) &&
                    !preReqOpt.optionGroup.options.some(
                      (preReqGroupRpo) =>
                        // And the prereq's optionGroup
                        // does not yet have a selected opt that already
                        // satisfies this RPO's prereqs
                        selectedRpos.includes(preReqGroupRpo.id) &&
                        data.rpo.optionPrerequisites.map((requiredRpo) => requiredRpo.id).includes(preReqGroupRpo.id),
                    ),
                ),
              ].map((opt) => opt.displayName);

              const disallowedInBaseConfig = !data.rpo.showInCheckout && isCreatingBaseConfig;
              return (
                <OptionSection
                  key={data.rpo.id}
                  configData={data}
                  conflicts={conflicts}
                  missingPreReqs={missingPreReqs}
                  disallowedInBaseConfig={disallowedInBaseConfig}
                  formState={formState}
                  recomputeCosts={recomputeCosts}
                  disabled={!data.rpo.active || missingPreReqs.nonEmpty || conflicts.nonEmpty || disallowedInBaseConfig}
                />
              );
            })}
        </div>
      ))}
    </div>
  );
}
